import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import DispersionEngine from "@/components/DispersionEngine";
import { API } from "@/lib/api";

/* Neural network particle canvas */
function NeuralCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let nodes = [];
    let w = 0;
    let h = 0;

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      w = r.width;
      h = r.height;
      const count = Math.max(40, Math.floor((w * h) / 12000));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: 1.4 + Math.random() * 1.2,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        a.x += a.vx;
        a.y += a.vy;
        if (a.x < 0 || a.x > w) a.vx *= -1;
        if (a.y < 0 || a.y > h) a.vy *= -1;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 130) {
            ctx.strokeStyle = `rgba(144,184,73,${(1 - d / 130) * 0.45})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        ctx.fillStyle = "rgba(144,184,73,0.9)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
}

function Typewriter({ words, className }) {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [del, setDel] = useState(false);

  useEffect(() => {
    const current = words[idx % words.length];
    const speed = del ? 40 : 80;
    const tt = setTimeout(() => {
      if (!del) {
        setText(current.slice(0, text.length + 1));
        if (text.length + 1 === current.length) setTimeout(() => setDel(true), 1500);
      } else {
        setText(current.slice(0, text.length - 1));
        if (text.length - 1 === 0) {
          setDel(false);
          setIdx((i) => i + 1);
        }
      }
    }, speed);
    return () => clearTimeout(tt);
  }, [text, del, idx, words]);

  return (
    <span className={className}>
      {text}
      <span className="inline-block w-1 h-7 bg-brand ml-1 animate-pulse align-middle" />
    </span>
  );
}

function HeroTechnology() {
  const { t } = useTranslation();
  let words = t("technology.hero.rotating", { returnObjects: true });
  if (!Array.isArray(words) || words.length === 0) words = ["sustainability.", "compliance.", "predictive risk.", "Africa."];

  return (
    <section className="relative pt-32 pb-xl overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-cream via-background to-surface-container-low" />

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-md items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block bg-primary-container text-brand text-label-caps px-3 py-1 rounded-full mb-sm">
              {t("technology.hero.eyebrow")}
            </span>
            <h1 className="font-h1 text-[40px] md:text-h1 text-primary mb-md">
              {t("technology.hero.title_a")}{" "}
              <Typewriter words={words} className="text-gradient" />
            </h1>
            <p className="text-body-lg text-on-surface-variant max-w-xl mb-lg">
              {t("technology.hero.intro")}
            </p>
            <div className="flex flex-wrap gap-sm">
              <a href="#stack" data-testid="tech-explore-api" className="bg-primary text-white px-6 py-3 rounded-lg font-manrope font-bold flex items-center gap-2 hover:opacity-90 active:scale-95 transition-all">
                {t("technology.hero.cta_api")} <span className="material-symbols-outlined">terminal</span>
              </a>
              <a href="#architecture" data-testid="tech-view-architecture" className="border border-outline-variant text-on-surface px-6 py-3 rounded-lg font-manrope font-bold hover:bg-surface-container transition-all">
                {t("technology.hero.cta_arch")}
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="relative aspect-square rounded-xl overflow-hidden shadow-2xl border border-outline-variant bg-[#0b0f08]"
          >
            <NeuralCanvas />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-zinc-300">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
                {t("technology.hero.neural_live")}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-brand font-mono">v4.2.0</div>
            </div>
            <div className="absolute bottom-6 left-6 right-6 glass-dark p-md rounded-lg text-white">
              <div className="flex justify-between items-center mb-xs">
                <span className="text-label-caps">{t("technology.hero.neural_load")}</span>
                <span className="text-label-caps font-bold text-brand">Ramping</span>
              </div>
              <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: "42%" }} transition={{ duration: 1.6, delay: 0.6, ease: "easeOut" }} className="bg-brand h-full" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CoreEngines() {
  const { t } = useTranslation();
  const apiBullets = t("technology.engines.api.bullets", { returnObjects: true }) || [];
  return (
    <section id="stack" className="max-w-[1280px] mx-auto px-6 mb-xl">
      <div className="flex justify-between items-end mb-lg">
        <div>
          <h2 className="font-h2 text-h2 text-primary">{t("technology.engines.title")}</h2>
          <p className="text-body-md text-on-surface-variant">{t("technology.engines.intro")}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6 }} className="md:col-span-2 glass-panel p-lg rounded-xl flex flex-col justify-between min-h-[320px] relative overflow-hidden group">
          <div className="relative z-10">
            <span className="material-symbols-outlined text-brand text-4xl mb-sm">analytics</span>
            <h3 className="font-h3 text-h3 text-primary mb-xs">{t("technology.engines.predictive.title")}</h3>
            <p className="text-body-md text-on-surface-variant max-w-md">{t("technology.engines.predictive.desc")}</p>
          </div>
          <div className="relative z-10 flex gap-4 mt-lg">
            <div className="text-center">
              <div className="text-h3 font-bold text-brand font-manrope">3M+</div>
              <div className="text-label-caps text-zinc-500">{t("technology.engines.predictive.ingestion")}</div>
            </div>
            <div className="w-px h-10 bg-zinc-200" />
            <div className="text-center">
              <div className="text-h3 font-bold text-brand font-manrope">30M+</div>
              <div className="text-label-caps text-zinc-500">{t("technology.engines.predictive.target")}</div>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: 0.1 }} className="bg-primary p-lg rounded-xl flex flex-col justify-between text-white relative overflow-hidden">
          <div className="absolute -bottom-12 -right-12 w-48 h-48 rounded-full bg-brand/10 blur-3xl" />
          <div className="relative z-10">
            <span className="material-symbols-outlined text-brand text-4xl mb-sm">verified_user</span>
            <h3 className="font-h3 text-h3 mb-xs">{t("technology.engines.compliance.title")}</h3>
            <p className="text-body-md text-zinc-400">{t("technology.engines.compliance.desc")}</p>
          </div>
          <div className="relative z-10 mt-lg pt-lg border-t border-zinc-800">
            <div className="flex items-center justify-between">
              <span className="text-label-caps">{t("technology.engines.compliance.alpha")}</span>
              <span className="text-brand font-bold">Live</span>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: 0.15 }} className="glass-panel p-lg rounded-xl">
          <span className="material-symbols-outlined text-brand text-4xl mb-sm">trending_up</span>
          <h3 className="font-h3 text-h3 text-primary mb-xs">{t("technology.engines.trend.title")}</h3>
          <p className="text-body-md text-on-surface-variant">{t("technology.engines.trend.desc")}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, delay: 0.2 }} className="md:col-span-2 glass-panel p-lg rounded-xl flex flex-col md:flex-row gap-lg">
          <div className="flex-1">
            <span className="material-symbols-outlined text-brand text-4xl mb-sm">cloud_sync</span>
            <h3 className="font-h3 text-h3 text-primary mb-xs">{t("technology.engines.api.title")}</h3>
            <p className="text-body-md text-on-surface-variant mb-md">{t("technology.engines.api.desc")}</p>
            <ul className="space-y-2">
              {apiBullets.map((tt, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-on-surface">
                  <span className="material-symbols-outlined icon-fill text-xs text-brand">check_circle</span>
                  {tt}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1 bg-zinc-950 rounded-lg p-md font-mono text-xs text-zinc-400 overflow-hidden">
            <div className="flex gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <div className="space-y-1">
              <p><span className="text-purple-400">POST</span> /v1/predictions/carbon</p>
              <p>{`{`}</p>
              <p className="pl-4">"asset_id": <span className="text-green-400">"TURBINE_77_A"</span>,</p>
              <p className="pl-4">"interval": <span className="text-green-400">"monthly"</span>,</p>
              <p className="pl-4">"confidence_threshold": <span className="text-blue-400">0.95</span></p>
              <p>{`}`}</p>
              <p className="text-zinc-600 mt-4">{t("technology.engines.api.response")}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Architecture() {
  const { t } = useTranslation();
  const inputs = [
    { icon: "sensors", label: t("technology.architecture.input_iot") },
    { icon: "public", label: t("technology.architecture.input_sat") },
    { icon: "database", label: t("technology.architecture.input_erp") },
  ];
  const outputs = [
    { icon: "dashboard", label: t("technology.architecture.out_admin") },
    { icon: "code", label: t("technology.architecture.out_api") },
    { icon: "description", label: t("technology.architecture.out_compliance") },
  ];
  return (
    <section id="architecture" className="max-w-[1280px] mx-auto px-6 mb-xl py-xl bg-primary rounded-2xl relative overflow-hidden">
      <div className="absolute inset-0 tech-grid-dark opacity-50" />
      <div className="relative z-10 text-center mb-lg">
        <span className="text-brand text-label-caps">{t("technology.architecture.eyebrow")}</span>
        <h2 className="font-h2 text-h2 text-white">{t("technology.architecture.title")}</h2>
      </div>
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-4 gap-8 items-center max-w-5xl mx-auto px-6">
        <div className="space-y-4">
          {inputs.map((s) => (
            <div key={s.label} className="bg-zinc-900 border border-zinc-800 p-sm rounded-lg text-center">
              <span className="material-symbols-outlined text-zinc-500 mb-xs">{s.icon}</span>
              <div className="text-zinc-400 text-xs font-bold uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="md:col-span-2 flex flex-col items-center">
          <div className="relative w-full aspect-video flex items-center justify-center">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-brand to-transparent opacity-30" />
              <div className="h-full w-px bg-gradient-to-b from-transparent via-brand to-transparent opacity-30 absolute" />
            </div>
            <motion.div animate={{ scale: [1, 1.05, 1], rotate: [0, 2, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="relative z-20 w-48 h-48 rounded-full border-2 border-brand bg-brand/10 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border border-dashed border-brand/40 orbit" />
              <div className="absolute -inset-3 rounded-full border border-dashed border-brand/20 orbit-reverse" />
              <div className="text-center">
                <span className="material-symbols-outlined icon-fill text-brand text-5xl">hub</span>
                <div className="text-white font-bold text-sm mt-2">{t("technology.architecture.core")}</div>
              </div>
            </motion.div>
          </div>
          <div className="flex gap-4 mt-8 flex-wrap justify-center">
            {["KUBERNETES", "TENSORFLOW", "APACHE KAFKA"].map((tt) => (
              <span key={tt} className="bg-zinc-800 text-zinc-400 text-[10px] px-2 py-1 rounded border border-zinc-700">{tt}</span>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {outputs.map((s) => (
            <div key={s.label} className="bg-zinc-900 border border-brand/30 p-sm rounded-lg text-center">
              <span className="material-symbols-outlined text-brand mb-xs">{s.icon}</span>
              <div className="text-zinc-100 text-xs font-bold uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureGrid() {
  const { t } = useTranslation();
  const items = t("technology.features", { returnObjects: true }) || [];
  return (
    <section className="max-w-[1280px] mx-auto px-6 mb-xl">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {items.map((it, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, delay: i * 0.08 }} className={`p-4 ${i > 0 ? "md:border-l border-zinc-200" : ""}`}>
            <h4 className="font-h3 text-lg text-primary mb-2">{it.t}</h4>
            <p className="text-sm text-on-surface-variant">{it.d}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const liveDashImages = [
  "/imagery/gocems-network-map.png",
  "/imagery/gocems-device-readings.png",
  "/imagery/gocems-timeseries.png",
  "/imagery/gocems-patterns.png",
  "/imagery/gocems-operator.png",
];

function LiveProductGallery() {
  const { t } = useTranslation();
  const screens = t("technology.gallery.screens", { returnObjects: true }) || [];

  return (
    <section id="product-gallery" data-testid="tech-product-gallery" className="max-w-[1280px] mx-auto px-6 mb-xl py-xl">
      <div className="max-w-3xl mb-lg">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-tertiary-fixed text-on-tertiary-container text-[10px] font-bold uppercase tracking-widest">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
          </span>
          {t("technology.gallery.eyebrow")}
        </span>
        <h2 className="font-h2 text-h2 text-primary mt-4">
          {t("technology.gallery.title_a")} <span className="text-gradient">{t("technology.gallery.title_accent")}</span>
        </h2>
        <p className="text-on-surface-variant mt-3 max-w-2xl">{t("technology.gallery.intro")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-gutter auto-rows-[minmax(0,auto)]">
        {screens.map((d, i) => {
          const span = i < 4 ? "lg:col-span-3" : "lg:col-span-6";
          const src = liveDashImages[i];
          return (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`${span} group relative bg-white rounded-2xl border border-outline-variant overflow-hidden hover:border-brand hover:shadow-xl transition-all`}
            >
              <a href={src} target="_blank" rel="noreferrer" aria-label={d.title} className="block relative bg-gradient-to-br from-zinc-100 to-zinc-50 cursor-zoom-in">
                <img src={src} alt={d.title} loading="lazy" className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.015]" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-950/80 backdrop-blur border border-white/15 text-[10px] uppercase tracking-widest text-white font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                  {d.badge}
                </span>
              </a>
              <figcaption className="p-4 md:p-5 border-t border-outline-variant/70">
                <div className="flex items-start gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-brand font-bold pt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div className="min-w-0">
                    <h3 className="font-manrope font-bold text-primary text-base md:text-lg leading-tight mb-1">{d.title}</h3>
                    <p className="text-sm text-on-surface-variant leading-relaxed">{d.blurb}</p>
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          );
        })}
      </div>

      <p className="text-xs text-on-surface-variant mt-md max-w-3xl">{t("technology.gallery.footnote")}</p>
    </section>
  );
}

function DispersionBlock() {
  const { t } = useTranslation();
  const specs = t("technology.dispersion.specs", { returnObjects: true }) || [];
  return (
    <div id="dispersion-engine" data-testid="tech-dispersion-section" className="max-w-[1280px] mx-auto px-6 pt-xl relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter mb-md items-end">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand text-[10px] font-bold uppercase tracking-widest">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
            </span>
            {t("technology.dispersion.eyebrow")}
          </span>
          <h2 className="font-h2 text-h2 text-white mt-4">
            {t("technology.dispersion.title_a")}{" "}
            <span className="bg-gradient-to-r from-brand via-emerald-300 to-teal-300 bg-clip-text text-transparent">
              {t("technology.dispersion.title_accent")}
            </span>
          </h2>
          <p className="text-zinc-300 mt-3 max-w-2xl">{t("technology.dispersion.intro")}</p>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 gap-2">
          {specs.map((s, i) => (
            <div key={i} className="bg-zinc-900/60 border border-zinc-800 rounded-xl px-3 py-2 backdrop-blur">
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{s.k}</div>
              <div className="text-white font-bold text-sm mt-0.5 leading-tight">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <DispersionEngine />

      <p className="text-xs text-zinc-500 mt-md max-w-3xl">{t("technology.dispersion.footnote")}</p>
    </div>
  );
}

const maintenanceImages = [
  "/imagery/asset-health/dashboard.png",
  "/imagery/asset-health/map.png",
  "/imagery/asset-health/alerts.png",
  "/imagery/asset-health/work-orders.png",
  "/imagery/asset-health/pipeline.png",
  "/imagery/asset-health/ccfmp.png",
  "/imagery/asset-health/ccfmp-view-assets.png",
  "/imagery/asset-health/ccfmp-view-calibrations.png",
  "/imagery/asset-health/ccfmp-view-models.png",
  "/imagery/asset-health/ccfmp-view-work-orders.png",
  "/imagery/asset-health/reports.png",
  "/imagery/asset-health/docs.png",
];

function MaintenanceBlock() {
  const { t } = useTranslation();
  const screens = t("technology.maintenance.screens", { returnObjects: true }) || [];
  return (
    <div id="maintenance-console" data-testid="tech-maintenance-console" className="max-w-[1280px] mx-auto px-6 pb-xl pt-lg relative z-10">
      <div className="max-w-3xl mb-lg">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/10 text-brand text-[10px] font-bold uppercase tracking-widest">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
          </span>
          {t("technology.maintenance.eyebrow")}
        </span>
        <h2 className="font-h2 text-h2 text-white mt-4">
          {t("technology.maintenance.title_a")}{" "}
          <span className="bg-gradient-to-r from-brand via-emerald-300 to-teal-300 bg-clip-text text-transparent">
            {t("technology.maintenance.title_accent")}
          </span>
        </h2>
        <p className="text-zinc-300 mt-3 max-w-2xl">{t("technology.maintenance.intro")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-gutter">
        {screens.map((d, i) => {
          const src = maintenanceImages[i];
          return (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="group relative bg-zinc-900/70 backdrop-blur border border-zinc-800 rounded-2xl overflow-hidden hover:border-brand/60 hover:shadow-[0_25px_60px_-20px_rgba(144,184,73,0.25)] transition-all"
            >
              <a href={src} target="_blank" rel="noreferrer" aria-label={d.title} data-testid={`maint-screen-${i}`} className="block relative bg-zinc-950 cursor-zoom-in">
                <img src={src} alt={d.title} loading="lazy" className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.01]" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-950/85 backdrop-blur border border-brand/40 text-[10px] uppercase tracking-widest text-brand font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                  {d.badge}
                </span>
              </a>
              <figcaption className="p-4 md:p-5 border-t border-zinc-800/80">
                <div className="flex items-start gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-brand font-bold pt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div className="min-w-0">
                    <h3 className="font-manrope font-bold text-white text-base leading-tight mb-1">{d.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">{d.blurb}</p>
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          );
        })}
      </div>

      <p className="text-xs text-zinc-500 mt-md max-w-3xl">{t("technology.maintenance.footnote")}</p>
    </div>
  );
}

function WaitlistForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setErrorMsg(t("waitlist.invalid_email"));
      setStatus("error");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/newsletter`, { email });
      setStatus("success");
      setEmail("");
      setTimeout(() => setStatus("idle"), 5000);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || t("waitlist.error_default"));
      setStatus("error");
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="waitlist-block">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand font-bold">{t("waitlist.title")}</div>
        <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">{t("waitlist.subtitle")}</span>
      </div>

      {status === "success" ? (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} data-testid="waitlist-success" className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-brand/15 border border-brand/40">
          <span className="material-symbols-outlined icon-fill text-brand text-[20px] shrink-0">check_circle</span>
          <div className="leading-tight">
            <div className="text-white text-sm font-bold">{t("waitlist.success_title")}</div>
            <div className="text-zinc-300 text-xs">{t("waitlist.success_body")}</div>
          </div>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="flex gap-2" data-testid="waitlist-form">
          <input
            type="email" required disabled={status === "loading"} value={email}
            onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
            placeholder={t("waitlist.placeholder")} data-testid="waitlist-email-input"
            className="flex-1 min-w-0 bg-zinc-900 border border-zinc-800 focus:border-brand focus:ring-2 focus:ring-brand/30 outline-none rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 transition-all"
          />
          <button type="submit" disabled={status === "loading"} data-testid="waitlist-submit" className="group relative shrink-0 px-4 py-2.5 rounded-lg bg-brand text-white text-sm font-bold flex items-center gap-1.5 hover:shadow-[0_0_20px_-4px_rgba(144,184,73,0.6)] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed transition-all">
            {status === "loading" ? (
              <>
                <span className="material-symbols-outlined text-[16px] animate-spin">progress_activity</span>
                <span>{t("waitlist.submitting")}</span>
              </>
            ) : (
              <>
                <span>{t("waitlist.submit")}</span>
                <span className="material-symbols-outlined text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
              </>
            )}
          </button>
        </form>
      )}

      {status === "error" && (
        <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} data-testid="waitlist-error" className="text-[11px] text-red-400 mt-2 font-mono">{errorMsg}</motion.p>
      )}

      <p className="text-[10px] text-zinc-600 mt-2 leading-relaxed">
        {t("waitlist.agree_pre")} <a href="/privacy" className="text-zinc-500 hover:text-brand underline">{t("waitlist.agree_link")}</a>{t("waitlist.agree_post")}
      </p>
    </div>
  );
}

function LaunchingSoon() {
  const { t } = useTranslation();
  const targetDate = useRef(new Date("2026-09-01T00:00:00Z"));
  const [timeLeft, setTimeLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, targetDate.current - new Date());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  let taglines = t("technology.launching.taglines", { returnObjects: true });
  if (!Array.isArray(taglines)) taglines = [];
  let modules = t("technology.launching.modules", { returnObjects: true });
  if (!Array.isArray(modules)) modules = [];

  return (
    <section id="launching-soon" data-testid="tech-launching-soon" className="relative bg-brand-dark text-white overflow-hidden">
      <div className="absolute inset-0 tech-grid-dark opacity-25 pointer-events-none" />
      <motion.div aria-hidden="true" className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-brand/15 blur-3xl pointer-events-none" animate={{ scale: [1, 1.2, 1], opacity: [0.6, 0.9, 0.6] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div aria-hidden="true" className="absolute -bottom-40 -right-40 w-[640px] h-[640px] rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" animate={{ scale: [1.1, 1, 1.1], opacity: [0.5, 0.8, 0.5] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} />

      <div className="relative z-10 pt-xl">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="relative rounded-full border border-brand/30 bg-zinc-950/60 backdrop-blur overflow-hidden mb-lg">
            <div className="flex gap-12 py-2.5 whitespace-nowrap animate-marquee">
              {[...taglines, ...taglines, ...taglines].map((tag, i) => (
                <span key={i} className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                  <span className="w-1 h-1 rounded-full bg-brand" />
                  <span className="text-brand font-bold">{tag}</span>
                </span>
              ))}
            </div>
            <div className="absolute top-0 bottom-0 left-0 w-20 bg-gradient-to-r from-brand-dark to-transparent pointer-events-none" />
            <div className="absolute top-0 bottom-0 right-0 w-20 bg-gradient-to-l from-brand-dark to-transparent pointer-events-none" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center mb-lg">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }} className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-brand/40 bg-gradient-to-r from-brand/25 via-emerald-400/15 to-teal-400/25 backdrop-blur">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand" />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white font-bold">{t("technology.launching.eyebrow")}</span>
                <span className="w-px h-4 bg-brand/40" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">{t("technology.launching.target")}</span>
              </motion.div>

              <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.1 }} className="font-h2 text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mt-5 leading-[1.05] tracking-tight">
                {t("technology.launching.title_a")}{" "}
                <span className="bg-gradient-to-r from-brand via-emerald-300 to-teal-300 bg-clip-text text-transparent hero-shimmer bg-[length:220%_100%]">{t("technology.launching.title_accent")}</span>{" "}
                <br />
                {t("technology.launching.title_b")}
              </motion.h2>

              <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.2 }} className="text-zinc-300 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
                {t("technology.launching.intro_pre")}{" "}
                <strong className="text-white">{t("technology.launching.intro_disp")}</strong>{" "}
                {t("technology.launching.intro_mid")}{" "}
                <strong className="text-white">{t("technology.launching.intro_maint")}</strong>{" "}
                {t("technology.launching.intro_post")}
              </motion.p>
            </div>

            <motion.div initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: 0.25 }} className="lg:col-span-5">
              <div className="relative rounded-2xl border border-brand/30 bg-zinc-950/70 backdrop-blur p-6 overflow-hidden">
                <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-brand/30 blur-3xl pointer-events-none" />
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand font-bold">{t("technology.launching.countdown")}</div>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-brand" />
                      </span>
                      {t("technology.launching.ticking")}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { k: t("technology.launching.days"), v: timeLeft.d },
                      { k: t("technology.launching.hrs"), v: timeLeft.h },
                      { k: t("technology.launching.min"), v: timeLeft.m },
                      { k: t("technology.launching.sec"), v: timeLeft.s },
                    ].map((u, i) => (
                      <div key={i} className="relative bg-zinc-900/80 border border-zinc-800 rounded-xl p-3 text-center">
                        <div className="font-manrope font-black text-white text-3xl md:text-4xl tabular-nums leading-none">{String(u.v).padStart(2, "0")}</div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{u.k}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-zinc-800 flex items-center gap-2">
                    <span className="material-symbols-outlined text-brand text-[16px] icon-fill">rocket_launch</span>
                    <span className="text-xs text-zinc-400">
                      {t("technology.launching.target_window")} <span className="text-white font-bold">{t("technology.launching.target")}</span>
                    </span>
                  </div>

                  <WaitlistForm />
                </div>
              </div>
            </motion.div>
          </div>

          <div className="flex flex-wrap gap-3 mb-md" data-testid="module-index">
            {modules.map((m, i) => (
              <motion.a
                key={i}
                href={i === 0 ? "#dispersion-engine" : "#maintenance-console"}
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, delay: 0.35 + i * 0.08 }}
                className="group flex items-center gap-3 px-4 py-3 rounded-xl border border-zinc-800 bg-zinc-950/60 backdrop-blur hover:border-brand/60 hover:bg-zinc-950/90 transition-all"
              >
                <span className="font-mono text-[11px] font-bold text-brand w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="w-px h-6 bg-zinc-800 group-hover:bg-brand transition-colors" />
                <span className="flex flex-col leading-tight">
                  <span className="font-manrope font-bold text-white text-sm">{m.label}</span>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{m.sub}</span>
                </span>
                <span className="material-symbols-outlined text-zinc-600 text-[16px] group-hover:text-brand group-hover:translate-x-0.5 transition-all">arrow_outward</span>
              </motion.a>
            ))}
          </div>
        </div>
      </div>

      <DispersionBlock />

      <div className="max-w-[1280px] mx-auto px-6 py-lg relative z-10">
        <div className="flex items-center gap-4">
          <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand font-bold">{t("technology.launching.module_label")} 02</span>
          <span className="flex-1 h-px bg-gradient-to-r from-brand/40 via-zinc-800 to-transparent" />
        </div>
      </div>

      <MaintenanceBlock />
    </section>
  );
}

export default function Technology() {
  return (
    <>
      <HeroTechnology />
      <CoreEngines />
      <Architecture />
      <LiveProductGallery />
      <LaunchingSoon />
      <FeatureGrid />
    </>
  );
}
